import React, {
  FC,
  useEffect,
  useRef,
  useCallback,
  KeyboardEventHandler,
  useState,
  RefObject,
} from 'react';

// Hooks
import useScrollLock from '../../hooks/useScrollLock';
import useFocusTrap from '../../hooks/useFocusTrap';
import useClickOutside from '../../hooks/useClickOutside';
import useForceUpdateOnWindowResize from '../../hooks/useForceUpdateOnWindowResize';

// Components
import { XIcon } from '../SvgIconsV3/X';
import ModalContainer from './ModalContainer';

import { Overlay, ModalDialog, CloseModalButton, ScreenReaderText } from './ModalV2.styles';

export const MODAL_ANIMATION_ENDED_EVENT = 'MODAL_ANIMATION_ENDED';

export type ModalV2Props = {
  /** A function to perform when the user closes the modal.  Should remove the modal from the DOM. */
  onClose: () => void;
  /** Where the modal will be mounted.  Should be at the root of the app so that the overlay covers the entire window.  Must be separately defined. */
  rootId: string;
  /** The id for the h1 (preferred) or h2 element that will be the title for the modal. If you cannot add a heading and make it visible, you may make the heading visible only to screen reader users. Accessibility Requirement */
  dialogHeadingId: string;
  /** The modal should open based on some user action, preferably from a button element.  Focus will return to the element upon closing the modal.   Accessibility requirement. */
  triggerRef: RefObject<HTMLButtonElement | HTMLAnchorElement | HTMLDivElement> | null;
  className?: string;

  /** Locks background scrolling in place.  Defaults to false in Modal and ModalV2.  We would like this to default to true, but the scroll lock jumps the background to the top top of the page, so there is a tradeoff to be mad. */
  lockBgScrolling?: boolean;

  /** Show the close button. Default true: show the close button */
  showCloseButton?: boolean;

  /** Should clicking outside the modal close it.  Default true: close the modal on click outside. */
  closeOnOverlayClick?: boolean;

  /** Should the modal close when the 'escape' key is press.  Default true: close the modal on escape. */
  closeOnEscape?: boolean;

  /** Focus trap takes a snapshot of focusable components when the Modal is enabled, so it is sometimes useful to disable for content that conditionally renders focusable elements */
  disableFocusTrap?: boolean;
};

/**
 * ModalV2 is a "drop-in" replacement for Modal.  This modal addresses some deficiencies
 * with ModalV1.
 *
 * In particular, the ModalV1:
 *
 * - does not support mobile styles
 * - adds padding to the content
 * - positions the close button in a way that does not allow for content in the modal to push to the edge.
 * - does not follow the design system for scrolling content
 *
 * For the best use of ModalV2, you should use ModalContent for the children
 *
 * Design system reference: [Components/Modal](https://www.figma.com/file/kzG9IDk1npM79QRzaU9XUh/Zola-Design-System-Documentation?node-id=101%3A940)
 *
 * @example
 * ```
 * {modalOpen ? (
 *   <ModalV2
 *     rootId={modalRootId}
 *     triggerRef={triggerRef}
 *     dialogHeadingId="modal-header"
 *     onClose={() => setModalOpen(false)}
 *   >
 *     <ModalContent
 *       className="modal-content blue-background-modal"
 *       title={{
 *         id: 'modal-header',
 *         hed: 'Lorem Ipsum Header',
 *       }}
 *       controls={renderControls()}
 *       alignControlsLeft
 *     />
 *   </ModalV2>
 * ) : null}
 * ```
 */
const ModalV2: FC<ModalV2Props> = ({
  children,
  dialogHeadingId = 'dialog-heading',
  onClose,
  rootId,
  triggerRef,
  className,
  lockBgScrolling = false,
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  disableFocusTrap = false,
}) => {
  useForceUpdateOnWindowResize();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const handleTabbing = useFocusTrap(dialogRef, disableFocusTrap);
  const { onDisableLock, onEnableLock } = useScrollLock(lockBgScrolling);

  useEffect(() => {
    onEnableLock();
    return () => {
      onDisableLock();
    };
  }, [onDisableLock, onEnableLock]);

  const onCloseCallback = useCallback(onClose, [onClose]);

  useClickOutside({
    ref: dialogRef,
    onClickOutside: onCloseCallback,
    isOpen: true && closeOnOverlayClick,
  });

  useEffect(() => {
    // Close modal when hitting "Esc".
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.preventDefault();
        event.stopPropagation();
        onCloseCallback();
      }
    };
    window.addEventListener('keydown', close, true);

    return function removeEventListenerAndReturnFocus() {
      window.removeEventListener('keydown', close, true);
    };
  }, [closeOnEscape, onCloseCallback, triggerRef]);

  useEffect(() => {
    if (disableFocusTrap && showCloseButton) {
      closeButtonRef.current?.focus();
    }
    const triggeringEl = triggerRef?.current;
    return () => {
      if (triggeringEl) {
        triggeringEl.focus();
      }
    };
  }, [disableFocusTrap, showCloseButton, triggerRef]);

  const handlePressEnter: KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onCloseCallback();
    }
  };

  const [isFullScreen, setIsFullScreen] = useState(false);
  const calculateTopPosition = () => {
    if (dialogRef.current) {
      const topPosition = dialogRef.current.offsetTop;
      if (topPosition === 0) {
        setIsFullScreen(true);
      } else {
        setIsFullScreen(false);
      }
    }
  };
  const onAnimationEnd = () => {
    calculateTopPosition();
    window.dispatchEvent(new CustomEvent(MODAL_ANIMATION_ENDED_EVENT));
  };

  useEffect(calculateTopPosition);

  return (
    <ModalContainer rootId={rootId}>
      <Overlay className={className} id="z-modal-overlay-v2">
        <ModalDialog
          role="dialog"
          aria-modal="true"
          aria-labelledby={dialogHeadingId}
          ref={dialogRef}
          onKeyDown={handleTabbing}
          squareCorners={isFullScreen}
          onAnimationEnd={onAnimationEnd}
        >
          {showCloseButton && (
            <CloseModalButton
              id="zCloseModal"
              onClick={onCloseCallback}
              onKeyDown={handlePressEnter}
              ref={closeButtonRef}
              data-testid="modal-close"
            >
              <XIcon height={20} width={20} showTitle={false} />
              <ScreenReaderText>Close</ScreenReaderText>
            </CloseModalButton>
          )}
          {children}
        </ModalDialog>
      </Overlay>
    </ModalContainer>
  );
};

export default ModalV2;
